'use client'

import { trackMarketingDemoInteracted } from '@/lib/analytics'
import { useState } from 'react'
import { SERIES_PALETTE } from '@/components/billing/analytics/colors'

// Mirrors the billing dashboard's "Spend over time": stacked bars, a User / Project / Model
// pivot, hover tooltips and a share table. One set of daily totals is re-sliced per pivot,
// so switching pivots keeps the bars' heights and only re-cuts the stack — as the real one does.

const PIVOTS = {
  User: ['Director', 'Editor', 'Claude Code', 'Codex'],
  Project: ['Spot 02', 'Pilot ep. 1', 'Lookbook', 'Tests'],
  Model: ['Veo 3.1', 'Seedance 2.5', 'MiniMax H3', 'FLUX.3'],
} as const
type Pivot = keyof typeof PIVOTS

const WEIGHTS: Record<Pivot, number[]> = {
  User: [0.3, 0.22, 0.36, 0.12],
  Project: [0.44, 0.28, 0.18, 0.1],
  Model: [0.38, 0.27, 0.21, 0.14],
}
const TOTALS = [312, 448, 296, 610, 402, 138, 96, 720, 655, 804, 590, 741, 210, 912]
const DATES = TOTALS.map((_, i) => `Sep ${15 + i}`)

// Split a day's total by the pivot's weights, with a deterministic per-day wobble so the
// stacks look like activity rather than a fixed ratio.
function slice(total: number, pivot: Pivot, day: number): number[] {
  const raw = WEIGHTS[pivot].map((w, i) => w * (1 + 0.45 * Math.sin(day * 1.7 + i * 2.1)))
  const sum = raw.reduce((a, b) => a + b, 0)
  return raw.map(r => Math.round((r / sum) * total))
}

const usd = (n: number) => `$${n.toLocaleString('en-US')}`
const MAX = Math.max(...TOTALS)
const GRID = [0, 250, 500, 750]

export function ProductionUsage() {
  const [pivot, setPivot] = useState<Pivot>('User')
  const [hover, setHover] = useState<number | null>(null)
  const names = PIVOTS[pivot]
  const days = TOTALS.map((t, i) => slice(t, pivot, i))
  const seriesTotals = names.map((_, i) => days.reduce((sum, d) => sum + d[i], 0))
  const grand = seriesTotals.reduce((a, b) => a + b, 0)

  return (
    <div className="grid gap-4 p-4 md:p-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="rounded-lg border border-white/[0.08] p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[13px] font-medium text-white/85">Spend over time</p>
            <p className="text-[11px] text-white/40">Sep 15 – 28 · by {pivot.toLowerCase()}</p>
          </div>
          <div className="inline-flex h-8 items-center gap-0.5 rounded-lg border border-white/[0.12] p-0.5">
            {(Object.keys(PIVOTS) as Pivot[]).map(p => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setPivot(p)
                  trackMarketingDemoInteracted({ demo: 'usage', action: 'pivot', tab: p })
                }}
                className={`h-full rounded-md px-2.5 text-[12px] transition-colors ${
                  p === pivot ? 'bg-white/10 text-white' : 'text-white/45 hover:text-white/80'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="flex h-44 gap-2">
            <div className="relative w-8 shrink-0 text-[10px] tabular-nums text-white/30">
              {GRID.map(g => (
                <span key={g} className="absolute right-0 -translate-y-1/2" style={{ bottom: `${(g / MAX) * 100}%` }}>
                  {usd(g)}
                </span>
              ))}
            </div>
            <div className="relative flex flex-1 items-end gap-1" onMouseLeave={() => setHover(null)}>
              {GRID.map(g => (
                <span
                  key={g}
                  className="pointer-events-none absolute inset-x-0 border-t border-white/[0.06]"
                  style={{ bottom: `${(g / MAX) * 100}%` }}
                />
              ))}
              {days.map((day, i) => (
                <div
                  key={i}
                  className="relative flex h-full flex-1 items-end"
                  onMouseEnter={() => setHover(i)}
                  onFocus={() => setHover(i)}
                  tabIndex={0}
                >
                  <span
                    className={`absolute inset-0 -mx-0.5 rounded-sm transition-colors ${hover === i ? 'bg-white/[0.05]' : ''}`}
                  />
                  <div
                    className="relative flex w-full flex-col-reverse overflow-hidden rounded-[3px]"
                    style={{ height: `${(TOTALS[i] / MAX) * 100}%` }}
                  >
                    {day.map((v, j) => (
                      <span
                        key={j}
                        className="transition-[flex-grow] duration-300"
                        style={{ flexGrow: v, backgroundColor: SERIES_PALETTE[j] }}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {hover !== null && (
                <div
                  className="pointer-events-none absolute top-0 z-10 w-[168px] rounded-md border border-white/10 bg-[#1a1a1a] px-3 py-2 text-[11px] shadow-xl"
                  style={
                    hover < 8 ? { left: `${((hover + 1) / 14) * 100}%` } : { right: `${((14 - hover) / 14) * 100}%` }
                  }
                >
                  <p className="mb-1.5 font-medium text-white/85">{DATES[hover]}, 2026</p>
                  {names.map((name, j) => (
                    <p key={name} className="flex items-center gap-2 py-0.5 text-white/65">
                      <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: SERIES_PALETTE[j] }} />
                      <span className="flex-1 truncate">{name}</span>
                      <span className="tabular-nums text-white/85">{usd(days[hover][j])}</span>
                    </p>
                  ))}
                  <p className="mt-1 flex justify-between border-t border-white/10 pt-1 text-white/45">
                    <span>Total</span>
                    <span className="tabular-nums text-white/85">{usd(TOTALS[hover])}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="ml-10 mt-1.5 flex justify-between text-[10px] tabular-nums text-white/30">
            {DATES.filter((_, i) => i % 3 === 0 || i === DATES.length - 1).map(d => (
              <span key={d}>{d}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.08] p-4">
        <div className="mb-1 grid grid-cols-[1fr_64px_48px] text-[11px] text-white/35">
          <span>{pivot}</span>
          <span className="text-end">Spend</span>
          <span className="text-end">Share</span>
        </div>
        {names.map((name, i) => {
          const share = seriesTotals[i] / grand
          return (
            <div key={name} className="border-t border-white/[0.06] py-2.5 text-[13px]">
              <div className="grid grid-cols-[1fr_64px_48px] items-center">
                <span className="flex items-center gap-2 truncate text-white/80">
                  <span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: SERIES_PALETTE[i] }} />
                  {name}
                </span>
                <span className="text-end tabular-nums text-white/70">{usd(seriesTotals[i])}</span>
                <span className="text-end tabular-nums text-white/45">{Math.round(share * 100)}%</span>
              </div>
              <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.06]">
                <div
                  className="h-full rounded-full transition-[width] duration-300"
                  style={{ width: `${share * 100}%`, backgroundColor: SERIES_PALETTE[i] }}
                />
              </div>
            </div>
          )
        })}
        <div className="mt-2 flex justify-between border-t border-white/[0.06] pt-2.5 text-[12px] text-white/45">
          <span>Total</span>
          <span className="tabular-nums text-white/80">{usd(grand)}</span>
        </div>
      </div>
    </div>
  )
}
